// Inventory · Stock.
// Answers: what do we have, what is on its way, what is running low, and is the price
// estimates use still what we pay? Click a material for everything about it.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { money, qty, toCents } from '../../lib/format';
import { useInventory, useVendors } from '../../lib/inventory';
import { useSession } from '../../lib/session';
import { Empty, Info, Loading, Panel, Tag } from '../../ui/kit';
import { printKanban } from './KanbanCard';
import { cap, useInventoryNames } from './names';

export function Stock({ openMaterial }: { openMaterial: (id: string) => void }) {
  const s = useSession();
  const names = useInventoryNames();
  const inv = useInventory();
  const vendors = useVendors();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [all, setAll] = useState(false);
  if (inv.loading) return <Loading />;

  const prices = s.can('see_prices');
  const low = new Map(inv.reorders.map((l) => [l.material_id, l]));
  const asked = new Set(inv.waitingReorders.map((r) => r.material_id as string));
  // tracked: it has a count, a safety stock, something on order, or a price that needs a look
  const tracked = inv.materials.filter((m) => Number(m.stock_on_hand) !== 0 || m.safety_stock !== null || inv.onOrder[m.id] || inv.prices.get(m.id)?.differs);
  const pool = filter || all ? inv.materials : tracked;
  const shown = pool.filter((m) => !filter || `${m.description} ${m.part_number ?? ''} ${vendors.name(m.vendor_id) ?? ''}`.toLowerCase().includes(filter.toLowerCase())).slice(0, 200);
  const flagged = inv.materials.filter((m) => inv.prices.get(m.id)?.differs).length;

  return (
    <Panel
      label={`${filter || all ? 'Materials' : 'Tracked materials'} (${shown.length}${pool.length > shown.length ? ` of ${pool.length}` : ''})${prices && flagged ? ` · ${flagged} with a price to look at` : ''}`}
      right={
        <span className="row mid" style={{ gap: 8 }}>
          <label className="check">
            <input type="checkbox" className="read" checked={all} onChange={(e) => setAll(e.target.checked)} /> show all {inv.materials.length}
          </label>
          <input type="search" placeholder="Find a material" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </span>
      }
    >
      {shown.length === 0 ? (
        <Empty>{filter ? 'No material matches.' : `Nothing is tracked yet: no material has a count or a ${names.safety_stock}. Find a material, or show all, and click it to set them.`}</Empty>
      ) : (
        <div className="tblwrap">
          <table>
            <thead>
              <tr>
                <th>Material</th>
                <th className="right">On hand</th>
                <th className="right">On order</th>
                <th className="right">{cap(names.safety_stock)}</th>
                <th className="right">{cap(names.order_quantity)}</th>
                <th>Unit</th>
                {prices && <th className="right">Estimating price</th>}
                {prices && (
                  <th className="right">
                    Last paid
                    <Info left>What the last purchase order paid for it. Flagged when it is not the estimating price; open the material to see the history and decide.</Info>
                  </th>
                )}
                <th>Vendor</th>
                <th>{cap(names.kanban_card)}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((m) => {
                const p = inv.prices.get(m.id);
                return (
                  <tr key={m.id} className="click" onClick={() => openMaterial(m.id)}>
                    <td>
                      {m.description} {m.tracking_mode === 'bin' && <Tag kind="off">bin</Tag>} {low.has(m.id) && <Tag kind="un">reorder {qty(low.get(m.id)!.qty)}</Tag>} {asked.has(m.id) && <Tag kind="un">reorder asked</Tag>}
                    </td>
                    <td className="right num">{m.tracking_mode === 'bin' ? '—' : qty(m.stock_on_hand)}</td>
                    <td className="right num">{inv.onOrder[m.id] ? qty(inv.onOrder[m.id]) : '—'}</td>
                    <td className="right num">{m.safety_stock === null ? '—' : qty(m.safety_stock)}</td>
                    <td className="right num">{m.order_quantity === null ? '—' : qty(m.order_quantity)}</td>
                    <td>{m.unit ?? '—'}</td>
                    {prices && <td className="right num">{m.price === null ? '—' : money(toCents(m.price))}</td>}
                    {prices && (
                      <td className="right num nowrap">
                        {p?.last_paid ? money(toCents(p.last_paid)) : '—'} {p?.differs && <Tag kind="un">differs</Tag>}
                      </td>
                    )}
                    <td>{vendors.name(m.vendor_id) ?? '—'}</td>
                    <td className="nowrap">
                      <button
                        type="button"
                        className="linklike"
                        title={`Print this item's ${names.kanban_card}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          void printKanban(m.id, navigate);
                        }}
                      >
                        Print card
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
